/**
 * API configuration for FoneXpress Front website
 */
const API_BASE_URL =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.MODE === "production"
        ? "https://fonexpress.barodaweb.org"
        : "http://localhost:7004");


export const API_URL = API_BASE_URL;

/**
 * Canonical public origin of the storefront (no trailing slash).
 * Used for <link rel="canonical"> so staging/preview hosts never
 * canonicalize to themselves.
 */
export const SITE_URL = (
    import.meta.env.VITE_SITE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/+$/, "");

/**
 * Build the canonical URL for a path. The legacy WordPress site (whose URLs
 * we preserve) used trailing slashes, so canonicals keep that form.
 */
export function canonicalUrl(pathname) {
    let path = pathname || "/";
    if (!path.startsWith("/")) path = `/${path}`;
    if (!path.endsWith("/")) path = `${path}/`;
    return `${SITE_URL}${path}`;
}

/** Ensure a storefront path uses the live trailing-slash form (query preserved). */
export function withTrailingSlash(path) {
    if (!path) return "/";
    const raw = String(path);
    const qIndex = raw.indexOf("?");
    const pathname = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
    const query = qIndex >= 0 ? raw.slice(qIndex) : "";
    let p = pathname.startsWith("/") ? pathname : `/${pathname}`;
    if (p.length > 1 && !p.endsWith("/")) p = `${p}/`;
    return `${p}${query}`;
}

const DEFAULT_SITE_NAME = "FoneXpress Kenya";

/**
 * Expand Yoast %%title%% / %%sitename%% templates; return "" if unusable.
 */
export function resolveSeoTitle(raw, { title = "", siteName = DEFAULT_SITE_NAME } = {}) {
    const input = String(raw || "").trim();
    if (!input) return "";
    if (!input.includes("%%")) return input;
    let out = input
        .replace(/%%title%%/gi, title || "")
        .replace(/%%name%%/gi, title || "")
        .replace(/%%page%%/gi, "")
        .replace(/%%sep%%/gi, "|")
        .replace(/%%sitename%%/gi, siteName || DEFAULT_SITE_NAME)
        .replace(/%%[a-z0-9_-]+%%/gi, "");
    out = out
        .replace(/\s+/g, " ")
        .replace(/\s*\|\s*/g, " | ")
        .replace(/^\s*\|\s*|\s*\|\s*$/g, "")
        .trim();
    if (!out) return "";
    if (title && out === (siteName || DEFAULT_SITE_NAME)) return "";
    return out;
}

/** Live product URL: /product/{slug}/ */
export function productPath(slug) {
    if (!slug) return withTrailingSlash("/products");
    return withTrailingSlash(`/product/${slug}`);
}

/**
 * Live brand listing URL (current nav form):
 * /product-category/all-products/{brandSlug}/
 */
export function brandPath(brandSlug) {
    if (!brandSlug) return withTrailingSlash("/product-category/all-products");
    return withTrailingSlash(`/product-category/all-products/${brandSlug}`);
}

/** Live category URL: /product-category/{seg1}/{seg2?}/{seg3?}/ */
export function categoryPath(...segments) {
    const parts = segments.flat().filter(Boolean);
    if (!parts.length) return withTrailingSlash("/product-category/all-products");
    return withTrailingSlash(`/product-category/${parts.join("/")}`);
}

/** True when a string looks like an image path/URL, not an internal page link. */
export function isLikelyImageSource(path) {
    if (!path) return false;
    const raw = String(path).trim();
    if (/^https?:\/\//i.test(raw)) return true;

    const lower = raw.replace(/\\/g, "/").toLowerCase();
    if (lower.includes("uploads/")) return true;
    if (/\.(webp|jpg|jpeg|png|gif|svg|avif)(\?.*)?$/i.test(lower)) return true;

    return false;
}

/**
 * Build a full URL for uploaded assets (products, banners, brands, etc.)
 * Normalizes Windows backslashes and leading slashes from the API.
 */
export function resolveAssetUrl(path) {
    if (!path) return "";
    const raw = String(path).trim();
    if (/^https?:\/\//i.test(raw)) return raw;

    let normalized = raw.replace(/\\/g, "/");
    const uploadsIdx = normalized.toLowerCase().indexOf("uploads/");
    if (uploadsIdx > 0) {
        normalized = normalized.slice(uploadsIdx);
    }
    normalized = normalized.replace(/^\/+/, "");

    if (!normalized) return "";

    const base = API_BASE_URL.replace(/\/+$/, "");
    return `${base}/${normalized}`;
}

/**
 * Rewrite asset paths inside HTML so description images load from the API
 * server (VITE_API_URL), not the Vite frontend — and never from the legacy
 * fonexpress.net WordPress host when a local uploads path is present.
 *
 * Handles:
 *  - relative uploads/... paths
 *  - absolute /uploads/... paths
 *  - leftover https://fonexpress.net/wp-content/uploads/... (passthrough only
 *    if still remote; prefer running rewriteRemoteCmsAssets.js)
 */
export function resolveHtmlAssetUrls(html) {
    if (!html) return "";
    const base = API_BASE_URL.replace(/\/+$/, "");
    let out = String(html);

    // Local uploads (relative or absolute) → API origin
    out = out.replace(
        /(src|href)=(["'])(?:https?:\/\/[^"'/]+)?\/?(uploads\/[^"']+)\2/gi,
        (_match, attr, quote, uploadPath) => {
            const cleaned = String(uploadPath).replace(/\\/g, "/").replace(/^\/+/, "");
            return `${attr}=${quote}${base}/${cleaned}${quote}`;
        }
    );

    return out;
}

export const ENDPOINTS = {
    BANNERS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/banners`,
    },
    BRANDS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/brands`,
    },
    HOME_SECTIONS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/home-sections`,
    },
    NAV_MENUS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/nav-menus`,
    },
    PRODUCTS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/products`,
        FILTERS: `${API_BASE_URL}/api/v1/public/products/filters`,
        SUGGEST: `${API_BASE_URL}/api/v1/public/products/suggest`,
    },
    PRODUCT_CATEGORIES: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/product-categories`,
    },
    OFFERS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/offers`,
        BY_SLUG: (slug) => `${API_BASE_URL}/api/v1/public/offers/${slug}`,
    },
    POLICIES: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/policies`,
        BY_SLUG: (slug) => `${API_BASE_URL}/api/v1/public/policies/slug/${slug}`,
    },
    BLOGS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/blogs`,
        BY_SLUG: (slug) => `${API_BASE_URL}/api/v1/public/blogs/slug/${slug}`,
    },
    ABOUT_US: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/about-us`,
    },
    SOCIAL_MEDIA: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/social-media`,
    },
    HOME_KPIS: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/home-kpis`,
    },
    SEO: {
        BY_URL: (url) =>
            `${API_BASE_URL}/api/v1/public/seo?url=${encodeURIComponent(url)}`,
    },
    LANGUAGES: {
        PUBLIC: `${API_BASE_URL}/api/v1/public/languages`,
    },
};

export const WHATSAPP_PHONE = '254788740000';

export default API_URL;
